import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ApolloError,
  useMutation,
  useQuery,
  useSubscription,
} from "@apollo/client";
import { DocumentNode } from "graphql";
import type { TFunction } from "i18next";
import { MY_TIMESHEET_GET_CLIENTS_LIST } from "my-timesheet/graphql";
import {
  MY_TIMESHEET_CLIENTS_SUBSCRIPTION,
  MY_TIMESHEET_CREATE_CLIENT,
  MY_TIMESHEET_UPDATE_CLIENT,
} from "my-timesheet/graphql";
import { useOcSnackbar } from "oc/hooks";

export interface UseMyTimesheetClientsListParams {
  page?: number;
  pageSize?: number;
  sort?: string;
  sortDirection?: string;
  searchText?: string;
  searchField?: string;
  validity?: boolean;
}

export interface Client {
  _id: string;
  [key: string]: any;
}

export interface ClientsList {
  rows: Client[];
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
  [key: string]: any;
}

interface ClientsListData {
  listMyTimesheetClients: ClientsList;
}

interface ClientsListVars extends UseMyTimesheetClientsListParams { }

export interface UseMyTimesheetClientsListResult {
  data: ClientsList;
  loading: boolean;
  error?: ApolloError;
  createMySheetClient: (options?: any) => Promise<any>;
  updateMySheetClient: (options?: any) => Promise<any>;
}

export const useMyTimesheetClientsList = ({
  page,
  pageSize,
  sort,
  sortDirection,
  searchText,
  searchField,
  validity,
}: UseMyTimesheetClientsListParams): UseMyTimesheetClientsListResult => {
  const { t }: { t: TFunction } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();

  const { data, loading, error, refetch } = useQuery<
    ClientsListData,
    ClientsListVars
  >(MY_TIMESHEET_GET_CLIENTS_LIST as DocumentNode, {
    fetchPolicy: "cache-and-network",
    variables: {
      page,
      pageSize,
      sort,
      sortDirection,
      searchText,
      searchField,
      validity,
    },
  });

  useSubscription(MY_TIMESHEET_CLIENTS_SUBSCRIPTION, {
    onData: () => {
      refetch();
    },
  });

  const [createMySheetClientMutation] = useMutation<any, any>(
    MY_TIMESHEET_CREATE_CLIENT as DocumentNode,
  );

  const createMySheetClient = useCallback(
    async (options?: any) => {
      try {
        const result = await createMySheetClientMutation(options);
        setOpenMessage({
          type: "success",
          message: t("MY_TIMESHEET_NEW_CLIENT_SUCCESSFUL_CREATED"),
        });
        refetch();
        return result;
      } catch (mutationError) {
        console.error({ mutationError });
        setOpenMessage({
          type: "error",
          message: t("MY_TIMESHEET_NEW_CLIENT_CREATE_FAILED"),
        });
        refetch();
        throw mutationError;
      }
    },
    [createMySheetClientMutation, setOpenMessage, t, refetch],
  );

  const [updateMySheetClientMutation] = useMutation<any, any>(
    MY_TIMESHEET_UPDATE_CLIENT as DocumentNode,
  );

  const updateMySheetClient = useCallback(
    async (options?: any) => {
      try {
        const result = await updateMySheetClientMutation(options);
        setOpenMessage({
          type: "success",
          message: t("MY_TIMESHEET_CLIENT_SUCCESSFUL_UPDATED"),
        });
        refetch();
        return result;
      } catch (mutationError) {
        console.error({ mutationError });
        setOpenMessage({
          type: "error",
          message: t("MY_TIMESHEET_CLIENT_UPDATE_FAILED"),
        });
        refetch();
        throw mutationError;
      }
    },
    [updateMySheetClientMutation, setOpenMessage, t, refetch],
  );

  const list = data?.listMyTimesheetClients ?? { rows: [] as Client[] };
  const parsedRows = list.rows.map((row) => ({
    ...row,
    code: row._id,
  }));

  const newData: ClientsList = {
    ...list,
    rows: parsedRows,
  };

  return {
    data: newData,
    loading,
    error,
    createMySheetClient,
    updateMySheetClient,
  };
};

export default useMyTimesheetClientsList;
